import React, { useEffect, useMemo, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "../firebase";
import { useAuth } from "../contexts/auth-context";
import toast from "react-hot-toast";
import client from "../contentful";
import { X } from "lucide-react";

const SESSION_KEY = "adminToolsToken";

export default function AdminTools() {
  const { currentUser } = useAuth();
  const [verified, setVerified] = useState(false);
  const [token, setToken] = useState(null);
  const [verifyOpen, setVerifyOpen] = useState(true);
  const [keyInput, setKeyInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [query, setQuery] = useState("");
  const [selectedEmails, setSelectedEmails] = useState(new Set());
  const [op, setOp] = useState("replace");
  // Bulk editor: add-by-id flow
  const [pendingInput, setPendingInput] = useState("");
  const [pendingIds, setPendingIds] = useState([]);
  // Cache of course id -> { name, found }
  const [courseMeta, setCourseMeta] = useState({});
  const [busyApply, setBusyApply] = useState(false);

  // Restore token if valid
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(SESSION_KEY);
      if (!raw) return;
      const obj = JSON.parse(raw);
      if (obj && obj.token && obj.expiresAt && Date.now() < obj.expiresAt) {
        setToken(obj.token);
        setVerified(true);
        setVerifyOpen(false);
      } else {
        sessionStorage.removeItem(SESSION_KEY);
      }
    } catch {}
  }, []);

  // Fetch users when verified
  useEffect(() => {
    if (!verified || !token) return;
    (async () => {
      try {
        setLoading(true);
        const listUsers = httpsCallable(functions, "listUsers");
        const res = await listUsers({ token });
        const data = res.data;
        if (!data || !data.ok) {
          const errCode = (data && data.error) || "Failed";
          if (errCode === "INVALID_OR_EXPIRED_TOKEN") {
            sessionStorage.removeItem(SESSION_KEY);
            setToken(null);
            setVerified(false);
            setVerifyOpen(true);
            toast.error("Session expired. Please re-verify.");
            return;
          }
          throw new Error(errCode);
        }
        setUsers(data.users || []);
        setFiltered(data.users || []);
        setSelectedEmails(new Set());
      } catch (e) {
        toast.error(String((e && e.message) || e));
      } finally {
        setLoading(false);
      }
    })();
  }, [verified, token]);

  // Resolve course names for all existing user course ids (deduped)
  useEffect(() => {
    if (!verified) return;
    const setIds = new Set();
    for (const u of users) {
      (u.courses || []).forEach((cid) => setIds.add(String(cid || "").trim()));
    }
    pendingIds.forEach((cid) => setIds.add(String(cid || "").trim()));
    const ids = Array.from(setIds).filter(Boolean);
    if (ids.length === 0) return;

    const toFetch = ids.filter((id) => !courseMeta[id]);
    if (toFetch.length === 0) return;

    const chunkSize = 50;
    const run = async () => {
      try {
        for (let i = 0; i < toFetch.length; i += chunkSize) {
          const chunk = toFetch.slice(i, i + chunkSize);
          const resp = await client.getEntries({
            content_type: "course",
            "sys.id[in]": chunk.join(","),
          });
          const foundMap = {};
          (resp.items || []).forEach((item) => {
            const id = item?.sys?.id;
            const name = item?.fields?.title || item?.fields?.name || id;
            if (id) foundMap[id] = { name, found: true };
          });
          const nextMeta = { ...courseMeta };
          // mark found
          Object.keys(foundMap).forEach((id) => (nextMeta[id] = foundMap[id]));
          // mark not found for rest of chunk
          for (const id of chunk) {
            if (!nextMeta[id])
              nextMeta[id] = { name: "Not found", found: false };
          }
          setCourseMeta(nextMeta);
        }
      } catch (e) {
        // Fallback: mark unknown
        const nextMeta = { ...courseMeta };
        for (const id of toFetch) {
          if (!nextMeta[id]) nextMeta[id] = { name: "Unknown", found: false };
        }
        setCourseMeta(nextMeta);
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, pendingIds, verified]);

  // Filter
  useEffect(() => {
    if (!query) {
      setFiltered(users);
      return;
    }
    const q = query.toLowerCase();
    setFiltered(
      users.filter(
        (u) =>
          (u.email || "").toLowerCase().includes(q) ||
          (u.displayName || "").toLowerCase().includes(q)
      )
    );
  }, [query, users]);

  const allOnPageSelected = useMemo(() => {
    if (!filtered || filtered.length === 0) return false;
    return filtered.every((u) => selectedEmails.has(u.email));
  }, [filtered, selectedEmails]);

  const toggleSelectAllOnPage = () => {
    const next = new Set(selectedEmails);
    if (allOnPageSelected) {
      filtered.forEach((u) => next.delete(u.email));
    } else {
      filtered.forEach((u) => next.add(u.email));
    }
    setSelectedEmails(next);
  };

  // Length is verifyAdminKey's call, not the client's — see useAdminToken.
  const handleVerify = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const verifyAdminKey = httpsCallable(functions, "verifyAdminKey");
      const res = await verifyAdminKey({ key: keyInput });
      const data = res.data;
      if (!data || !data.ok)
        throw new Error((data && data.error) || "Verify failed");
      const payload = { token: data.token, expiresAt: data.expiresAt };
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(payload));
      setToken(data.token);
      setVerified(true);
      setVerifyOpen(false);
      setKeyInput("");
      toast.success("Verified");
    } catch (e) {
      toast.error(String((e && e.message) || e));
    } finally {
      setLoading(false);
    }
  };

  const applyUpdate = async () => {
    if (selectedEmails.size === 0) {
      toast.error("No users selected");
      return;
    }
    if (op !== "remove" && pendingIds.length === 0) {
      toast.error("No course IDs");
      return;
    }
    try {
      setBusyApply(true);
      const updateUsersCourses = httpsCallable(functions, "updateUsersCourses");
      const emails = Array.from(selectedEmails);
      const res = await updateUsersCourses({
        token,
        emails,
        op,
        courseIds: pendingIds,
      });
      const data = res.data;
      if (!data || !data.ok) {
        const errCode = (data && data.error) || "Update failed";
        if (errCode === "INVALID_OR_EXPIRED_TOKEN") {
          sessionStorage.removeItem(SESSION_KEY);
          setToken(null);
          setVerified(false);
          setVerifyOpen(true);
          toast.error("Session expired. Please re-verify.");
          return;
        }
        throw new Error(errCode);
      }
      toast.success(
        `Updated: ${data.updated}, Errors: ${data.errors?.length || 0}`
      );
      // Refresh
      const listUsers = httpsCallable(functions, "listUsers");
      const res2 = await listUsers({ token });
      const data2 = res2.data;
      if (data2 && data2.ok) {
        setUsers(data2.users || []);
        setFiltered(data2.users || []);
        setSelectedEmails(new Set());
      }
    } catch (e) {
      toast.error(String((e && e.message) || e));
    } finally {
      setBusyApply(false);
    }
  };

  const addPendingId = async () => {
    const raw = (pendingInput || "").trim();
    if (!raw) return;
    const parts = raw
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    if (parts.length === 0) {
      setPendingInput("");
      return;
    }
    const existing = new Set(pendingIds);
    const toAdd = parts.filter((id) => !existing.has(id));
    if (toAdd.length === 0) {
      setPendingInput("");
      return;
    }
    setPendingIds([...pendingIds, ...toAdd]);
    setPendingInput("");
    // trigger resolver via useEffect; optimistically mark as resolving
    setCourseMeta((m) => {
      const next = { ...m };
      toAdd.forEach((id) => {
        next[id] = next[id] || { name: "Resolving...", found: false };
      });
      return next;
    });
  };

  const removePendingId = (id) => {
    setPendingIds((arr) => arr.filter((x) => x !== id));
  };

  // Gate: render only verify until authorized
  if (!verified) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <input
          type="checkbox"
          className="modal-toggle"
          checked={verifyOpen}
          readOnly
        />
        <div className="modal" role="dialog">
          <div className="modal-box">
            <h3 className="font-bold text-lg">Admin Verification</h3>
            <p className="py-2 text-sm">
              Enter the temporary admin key to proceed.
            </p>
            <form onSubmit={handleVerify}>
              <input
                type="password"
                className="input input-bordered w-full"
                placeholder="Enter admin key"
                value={keyInput}
                onChange={(e) => setKeyInput(e.target.value)}
              />
              <div className="modal-action">
                <button
                  type="submit"
                  className={`btn btn-primary ${loading ? "loading" : ""}`}
                >
                  Verify
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="p-4 flex flex-col lg:flex-row gap-4 max-h-screen overflow-y-auto">
        {/* Left panel: Bulk editor */}
        <div className="w-full lg:w-4/12 min-w-[20rem] lg:sticky lg:top-0 self-start ">
          <div className="card bg-base-100 shadow">
            <div className="card-body gap-3">
              <h2 className="card-title">Bulk Edit Courses</h2>
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Add course ID</span>
                </label>
                <div className="join w-full">
                  <input
                    className="input input-bordered join-item w-full"
                    placeholder="courseId"
                    value={pendingInput}
                    onChange={(e) => setPendingInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addPendingId();
                      }
                    }}
                  />
                  <button
                    onClick={addPendingId}
                    className="btn btn-primary join-item"
                  >
                    +
                  </button>
                </div>
                {pendingIds.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {pendingIds.map((id) => (
                      <div key={id} className="flex flex-row gap-2">
                        <div className="flex flex-col gap-2 flex-1">
                          <span className="text-sm font-bold">
                            {courseMeta[id]?.name || "Resolving..."}
                          </span>
                          <span className="badge badge-outline self-end">
                            {id}
                          </span>
                        </div>
                        <button
                          onClick={() => removePendingId(id)}
                          className="btn btn-xs btn-ghost h-full"
                        >
                          <X />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Operation</span>
                </label>
                <select
                  className="select select-bordered"
                  value={op}
                  onChange={(e) => setOp(e.target.value)}
                >
                  <option value="replace">Replace</option>
                  <option value="add">Add</option>
                  <option value="remove">Remove</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={applyUpdate}
                  className={`btn flex-1 btn-primary ${
                    busyApply ? "loading" : ""
                  }`}
                >
                  Apply to selected
                </button>
                <button
                  onClick={() => setPendingIds([])}
                  className="btn flex-1 btn-ghost"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Right panel: Users table */}
        <div className="lg:flex-1">
          <div className="card bg-base-100 shadow">
            <div className="card-body gap-3">
              <div className="flex gap-2 items-center">
                <input
                  className="input input-bordered w-full"
                  placeholder="Search email or name"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                <button
                  onClick={toggleSelectAllOnPage}
                  className="btn btn-outline"
                >
                  {allOnPageSelected ? "Unselect all" : "Select all"}
                </button>
              </div>
              <div className="space-y-4">
                {loading ? (
                  <div className="flex items-center justify-center p-6">
                    <span className="loading loading-spinner" />
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="text-center p-6">No users</div>
                ) : (
                  filtered.map((u, i) => {
                    const checked = selectedEmails.has(u.email);
                    return (
                      <div className="flex flex-col gap-4" key={i}>
                        <div
                          key={u.email}
                          className={`card bg-base-100 ${
                            checked ? "opacity-100" : "opacity-50"
                          }`}
                        >
                          <div className="card-body p-0 gap-2">
                            <div className="flex items-center gap-3">
                              <input
                                type="checkbox"
                                id={u.email}
                                checked={checked}
                                onChange={(e) => {
                                  const next = new Set(selectedEmails);
                                  if (e.target.checked) next.add(u.email);
                                  else next.delete(u.email);
                                  setSelectedEmails(next);
                                }}
                                className="checkbox"
                              />
                              <label
                                htmlFor={u.email}
                                className="font-mono text-sm break-all"
                              >
                                {u.email}
                              </label>
                            </div>
                            <div className="flex flex-wrap gap-2 flex-col">
                              {(() => {
                                const current = u.courses || [];
                                const currentSet = new Set(current);
                                const isSelected = selectedEmails.has(u.email);
                                let items = [];
                                if (
                                  isSelected &&
                                  op === "replace" &&
                                  pendingIds.length > 0
                                ) {
                                  items = pendingIds.map((cid) => ({
                                    cid,
                                    kind: "info",
                                  }));
                                } else if (
                                  isSelected &&
                                  op === "add" &&
                                  pendingIds.length > 0
                                ) {
                                  const toAdd = pendingIds.filter(
                                    (id) => !currentSet.has(id)
                                  );
                                  items = [
                                    ...current.map((cid) => ({
                                      cid,
                                      kind: "normal",
                                    })),
                                    ...toAdd.map((cid) => ({
                                      cid,
                                      kind: "success",
                                    })),
                                  ];
                                } else if (
                                  isSelected &&
                                  op === "remove" &&
                                  pendingIds.length > 0
                                ) {
                                  items = current.map((cid) => ({
                                    cid,
                                    kind: pendingIds.includes(cid)
                                      ? "error"
                                      : "normal",
                                  }));
                                } else {
                                  items = current.map((cid) => ({
                                    cid,
                                    kind: "normal",
                                  }));
                                }

                                return items.map(({ cid, kind }) => {
                                  const meta = courseMeta[cid];
                                  const borderClass =
                                    kind === "info"
                                      ? "border-2 border-info/70"
                                      : kind === "success"
                                      ? "border-2 border-success/70"
                                      : kind === "error"
                                      ? "border-2 border-error/70"
                                      : "border border-base-300";
                                  const nameClass =
                                    kind === "info"
                                      ? "text-info"
                                      : kind === "success"
                                      ? "text-success"
                                      : kind === "error"
                                      ? "text-error"
                                      : "";
                                  return (
                                    <div
                                      key={`${cid}-${kind}`}
                                      className={`flex flex-col gap-2 rounded px-4 py-2 w-full ${borderClass}`}
                                    >
                                      <span
                                        className={`text-sm font-bold ${nameClass}`}
                                      >
                                        {meta?.name || "Resolving..."}
                                      </span>
                                      <span className="badge badge-outline self-end">
                                        {cid}
                                      </span>
                                    </div>
                                  );
                                });
                              })()}
                            </div>
                          </div>
                        </div>
                        {i !== filtered.length - 1 && (
                          <div
                            className="divider"
                            key={`${u.email}-divider`}
                          />
                        )}
                      </div>
                    );
                  })
                )}
              </div>
              <div className="flex items-center justify-between">
                <div>
                  Total:{" "}
                  <span className="badge badge-info">{users.length}</span>
                </div>
                <div>
                  Selected:{" "}
                  <span className="badge badge-info">
                    {selectedEmails.size}
                  </span>
                </div>
                <button
                  onClick={() => setVerified(false)}
                  className="btn btn-error btn-outline"
                >
                  Re-verify
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// 6J6U8qaKgOHkVkDFl2J5XL, 5qklXYSGLUqWZIQGqtTNfQ, 4tA7leF1N9GZ5SRHrcQqNM, 1fQji5AyGE8Su6CvNVzL83, 638zftLMxAF8A7sIJt4fE0, 2DVEm46ZohxT1gxziIL48E, 6nj44IrCp726fZ41iQX12x, 5V25xE2Q4Oz6v9g0dzEfSL, V8iNCKJUzbdRYFh65c24n, 7J8iQg35PfaXrqV5zzBwzh, DUffls9KdqTyG4Jpnp3D8, 6zSt7PNmDnQOFk3nLSbWhM, 594iNQWEWFFDvAHvkvtYiS
